import React from 'react';

import { useFormState } from 'react-final-form';
import { useDebounce } from 'use-debounce';

import { useVerificationRequestTotalChargeQuery } from 'src/api';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { convertEmployerListItemsToEmployerDataItems } from 'src/utils/employer';

import { VerificationRequestCreateFormValues } from '../types';

import { PaymentBlock } from './PaymentBlock';

export const VerificationRequestCreatePaymentBlock: React.FC = () => {
  const { values, submitting } = useFormState<VerificationRequestCreateFormValues>();

  const { isEmployeeExists, isEmployeeFrozen, setIsTotalChargeLoading } =
    useVerificationRequestFormData();

  const [{ ssn, verificationReportType, permissiblePurposeId, employers }] = useDebounce(
    values,
    DEBOUNCE_DELAY,
  );

  const { data, loading } = useVerificationRequestTotalChargeQuery({
    variables: {
      employeeSsn: ssn,
      requestType: verificationReportType,
      permissiblePurposeId,
      companies: convertEmployerListItemsToEmployerDataItems(employers),
    },
    skip: !ssn || !verificationReportType || !permissiblePurposeId || employers.length === 0,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: it's safe
  React.useEffect(() => {
    setIsTotalChargeLoading(loading);
  }, [loading]);

  const { totalCharge, isReverification } = React.useMemo(
    () =>
      data?.getVerificationRequestTotalCharge || {
        totalCharge: 0,
        isReverification: false,
      },
    [data?.getVerificationRequestTotalCharge],
  );

  if (!isEmployeeExists || isEmployeeFrozen) return null;

  return (
    <PaymentBlock
      totalCharge={totalCharge}
      isReverification={isReverification}
      loading={loading}
      submitting={submitting}
    />
  );
};

const DEBOUNCE_DELAY = 1000;
